import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Container, Row, Col, Pagination } from 'react-bootstrap';
import { FaMagnifyingGlass, FaNewspaper } from 'react-icons/fa6';
import Seo from '@/components/seo';
import PostCard from '@/components/blog/post-card';
import CategoryNav from '@/components/blog/category-nav';
import BlogCta from '@/components/blog/blog-cta';
import { blogRepository } from '@/repositories/blog';

export default function BlogIndexPage({ posts = [], total = 0, page = 1, totalPages = 1, categories = [], search = '' }) {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState(search || '');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      router.push(`/blog?search=${encodeURIComponent(searchTerm.trim())}`);
    } else {
      router.push('/blog');
    }
  };

  const handlePageChange = (newPage) => {
    const query = { ...router.query, page: newPage };
    router.push({ pathname: '/blog', query });
  };

  const featuredPost = page === 1 && !search && posts.length > 0 ? posts[0] : null;
  const gridPosts = featuredPost ? posts.slice(1) : posts;

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog AboutCar',
    description: 'Dicas, consultas, curiosidades e novidades do mercado automotivo para quem vai comprar ou vender veículos.',
    url: 'https://aboutcar.com.br/blog',
    publisher: {
      '@type': 'Organization',
      name: 'AboutCar',
      logo: {
        '@type': 'ImageObject',
        url: 'https://aboutcar.com.br/assets/images/aboutcar-classifieds.png',
      },
    },
  };

  return (
    <div className="blog-index-page py-4 bg-light-subtle" style={{ minHeight: '80vh' }}>
      <Seo
        title={search ? `Busca por "${search}" | Blog AboutCar` : 'Blog AboutCar | Dicas, Notícias e Segurança Veicular'}
        description="Fique por dentro das melhores dicas sobre histórico veicular, transferência, leilão, manutenção e mercado automotivo com os especialistas da AboutCar."
        canonical="https://aboutcar.com.br/blog"
        schema={websiteSchema}
      />

      <Container>
        {/* Header Hero */}
        <div className="text-center py-4 mb-3">
          <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fw-bold text-uppercase fs-7 mb-2 d-inline-flex align-items-center gap-1 shadow-sm">
            <FaNewspaper size={13} /> Blog Oficial AboutCar
          </span>
          <h1 className="display-5 fw-bold text-dark mt-2 mb-3">
            Notícias, Dicas &amp; Inteligência Automotiva
          </h1>
          <p className="lead text-secondary mx-auto fs-5" style={{ maxWidth: '640px' }}>
            Informações confiáveis para você tomar as melhores decisões na compra e venda do seu veículo seminovo.
          </p>

          {/* Search bar */}
          <div className="mx-auto mt-4" style={{ maxWidth: '540px' }}>
            <form onSubmit={handleSearchSubmit} className="position-relative">
              <input
                type="text"
                className="form-control form-control-lg rounded-pill shadow-sm ps-4 pe-5 border-0"
                placeholder="Buscar por placa, modelo, multas, leilão..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                type="submit"
                className="btn btn-primary rounded-circle position-absolute end-0 top-50 translate-middle-y me-2 d-flex align-items-center justify-content-center shadow-sm"
                style={{ width: 40, height: 40 }}
                title="Buscar"
              >
                <FaMagnifyingGlass size={15} />
              </button>
            </form>
          </div>
        </div>

        {/* Category Navigation Pills */}
        <CategoryNav categories={categories} activeSlug={null} />

        {/* Search Results Notice */}
        {search && (
          <div className="d-flex align-items-center justify-content-between alert alert-light rounded-4 shadow-sm mb-4 px-4 py-3">
            <span>
              Resultados para <strong>&ldquo;{search}&rdquo;</strong> ({total} {total === 1 ? 'artigo encontrado' : 'artigos encontrados'})
            </span>
            <Link href="/blog" className="btn btn-sm btn-outline-secondary rounded-pill px-3">
              Limpar busca
            </Link>
          </div>
        )}

        {/* Featured Post (only on page 1 without search) */}
        {featuredPost && (
          <div className="mb-5">
            <PostCard post={featuredPost} featured />
          </div>
        )}

        {/* Posts Grid */}
        {gridPosts.length > 0 ? (
          <Row className="g-4">
            {gridPosts.map((post) => (
              <Col key={post.id} xs={12} md={6} lg={4}>
                <PostCard post={post} />
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-center py-5 bg-white rounded-4 shadow-sm my-4">
            <h4 className="fw-bold text-dark mb-2">Nenhum artigo encontrado</h4>
            <p className="text-muted mb-4">Tente buscar por outro termo ou explore as categorias acima.</p>
            <Link href="/blog" className="btn btn-primary rounded-pill px-4 fw-semibold">
              Ver todos os artigos
            </Link>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-5">
            <Pagination className="shadow-sm rounded-pill overflow-hidden">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />

              {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .map((p, idx, arr) => {
                  const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <Pagination.Ellipsis disabled />}
                      <Pagination.Item active={p === page} onClick={() => handlePageChange(p)}>
                        {p}
                      </Pagination.Item>
                    </React.Fragment>
                  );
                })}

              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} />
              <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} />
            </Pagination>
          </div>
        )}

        {/* Conversion Banner */}
        <BlogCta />
      </Container>
    </div>
  );
}

export async function getServerSideProps({ query, res }) {
  res.setHeader('Cache-Control', 'public, s-maxage=1800, stale-while-revalidate=86400');

  const page = parseInt(query.page || '1', 10);
  const search = query.search || '';
  const limit = page === 1 && !search ? 10 : 9;

  const result = blogRepository.getPosts({ page, limit, search });
  const categories = blogRepository.getCategories();

  return {
    props: {
      posts: result.posts,
      total: result.total,
      page: result.page,
      totalPages: result.totalPages,
      categories,
      search,
    },
  };
}
